import React, { useState } from 'react';
import {
    Box, Card, TextField, Typography, CircularProgress, Grid,
    Button, Alert,
} from '@mui/material';
import { Image } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryProductVersions } from 'src/hooks/useQueryProductVersions';
import { useQueryDesignContent } from 'src/hooks/useQueryDesignContent';
import { ScoreControls } from './ScoreControls';
import { CommentBox } from './CommentBox';

const ImageContainer = styled(Box)(() => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    overflow: 'hidden',
}));

const StyledImage = styled('img')(() => ({
    width: '100%',
    maxHeight: '300px',
    objectFit: 'contain',
}));

enum RelevanceScore {
    NotScored = 'NotScored',
    NotRelevant = 'NotRelevant',
    Relevant = 'Relevant',
    HighlyRelevant = 'HighlyRelevant',
}

interface PropTypes {
    productKey: string;
    culture: string;
    scoredEntities: Models.IngestConfigApi.RelevanceTests.ScoredEntity[];
    onEntityAdded: (entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity) => void;
}

export const NewEntityCard: React.FC<PropTypes> = ({
    productKey,
    culture,
    onEntityAdded,
    scoredEntities,
}): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const [designConceptId, setDesignConceptId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [entityComment, setEntityComment] = useState('');
    const [entityScore, setEntityScore] = useState<Models.IngestConfigApi.RelevanceTests.RelevanceScore>(
        RelevanceScore.NotScored,
    );

    const { data: productVersionsData, isLoading: isLoadingVersions } = useQueryProductVersions(
        accessToken,
        { productKey },
        {
            enabled: !!accessToken && !!productKey && !!culture && !!designConceptId,
            onError: (err) => {
                setError(`Error fetching product versions: ${err.message}`);
            },
        },
    );

    const activeVersion = productVersionsData?.result?.[0]?.version?.toString();

    const { data: designContentData, isLoading: isLoadingDesignContent } = useQueryDesignContent(
        accessToken,
        productKey,
        activeVersion || '',
        culture,
        designConceptId,
        undefined,
        {
            enabled: !!accessToken && !!productKey && !!activeVersion && !!culture && !!designConceptId,
            onError: (err) => {
                setError(`Error fetching design content: ${err.message}`);
            },
            suspense: false,
        },
    );

    const isAlreadyScored = !!designConceptId && scoredEntities.some(
        (entity) => entity.designConceptId === designConceptId,
    );

    type ScoredEntity = Models.IngestConfigApi.RelevanceTests.ScoredEntity;
    const entityData: ScoredEntity | null = ((): ScoredEntity | null => {
        if (isAlreadyScored) {
            return null;
        }

        if (!designContentData?.results?.content?.[0]) {
            return null;
        }

        const content = designContentData.results.content[0];

        return {
            entityId: content.designId,
            designConceptId,
            keywords: content.keywords,
            categories: content.categoryNames,
            previewUrl: `https://${content.previewUrls.size1x}`,
            relevance: RelevanceScore.NotScored,
            reason: null,
        };
    })();

    const isLoading = isLoadingVersions || isLoadingDesignContent;
    const shouldShowData = !isLoading && !!entityData;

    const handleDesignConceptIdChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setDesignConceptId(e.target.value);
        setError(null);

        if (scoredEntities.some((entity) => entity.designConceptId === e.target.value)) {
            setError('Design concept already scored');
        }
    };

    const handleScoreChange = (
        _entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
        score: Models.IngestConfigApi.RelevanceTests.RelevanceScore,
    ): void => {
        setEntityScore(score);
    };

    const handleCommentChange = (
        _entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
        newComment: string,
    ): void => {
        setEntityComment(newComment);
    };

    const handleAddEntity = (): void => {
        if (entityData) {
            const entityToAdd = {
                ...entityData,
                relevance: entityScore,
                reason: entityComment || null,
            };

            onEntityAdded(entityToAdd);

            setDesignConceptId('');
            setEntityComment('');
            setEntityScore(RelevanceScore.NotScored);
        }
    };

    const displayEntity = entityData ? {
        ...entityData,
        relevance: entityScore,
        reason: entityComment || null,
    } : null;

    return (
        <Card
            sx={{
                mb: 2,
                borderRadius: '12px',
                boxShadow: '0 6px 16px rgba(0,0,0,0.12), 0 3px 6px rgba(0,0,0,0.08)',
            }}
        >
            <Grid container alignItems="flex-start">
                {/* Left Column - Image */}
                <Grid item p={2} xs={6}>
                    <ImageContainer>
                        {displayEntity ? (
                            <StyledImage
                                alt={displayEntity.designConceptId}
                                src={displayEntity.previewUrl}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '300px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: 'action.hover',
                                }}
                            >
                                <Image sx={{ color: 'text.disabled', fontSize: 32, mb: 1 }} />
                            </Box>
                        )}
                    </ImageContainer>
                </Grid>
                <Grid item p={2} xs={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="h6">Add New Concept</Typography>

                        {error && <Alert severity="error">{error}</Alert>}

                        <TextField
                            fullWidth
                            label="Design Concept ID"
                            size="small"
                            value={designConceptId}
                            onChange={handleDesignConceptIdChange}
                        />
                        {isLoading && designConceptId.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                        {shouldShowData && displayEntity && (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="body2">
                                        <strong>Keywords:</strong>
                                        {' '}
                                        {Array.isArray(displayEntity?.keywords)
                                            ? displayEntity.keywords.join(', ')
                                            : displayEntity?.keywords}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Categories:</strong>
                                        {' '}
                                        {Array.isArray(displayEntity?.categories)
                                            ? displayEntity.categories.join(', ')
                                            : displayEntity?.categories}
                                    </Typography>
                                </Box>
                                <ScoreControls
                                    entity={displayEntity!}
                                    onScoreChange={handleScoreChange}
                                />
                                <CommentBox
                                    entity={displayEntity!}
                                    onCommentChange={handleCommentChange}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <Button
                                        color="primary"
                                        disabled={entityScore === RelevanceScore.NotScored}
                                        variant="contained"
                                        onClick={handleAddEntity}
                                    >
                                        Add Entity
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Card>
    );
};
